import type { ReactNode } from 'react';
import { StatusBadge, type Zustand } from './StatusBadge';
import { WertEinheit } from './WertEinheit';
import './muster.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * KENNZAHLKARTE (Auftrag 17.09.2026, Abschnitt 9)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   „Mittlere Beleuchtungsstärke 522 lx, +12 % zum Sollwert;
 *    Blendungsbewertung UGR 16,8; Normkonformität 98 %, 56 von 57 Räumen.
 *    […] klare Hierarchie, nicht zu große Karten, wenig visuelles Rauschen.
 *    […] Keine dekorative Ampel-Farbgebung ohne Bedeutung."
 *
 * Deshalb:
 * - `zustand` ist optional. Ohne Bewertung bleibt die Karte farblos. Eine
 *   Karte, die nur zählt („57 Räume"), bekommt KEINE Farbe.
 * - Wo ein Zustand gesetzt ist, steht er als Marke mit Zeichen UND Text
 *   (Abschnitt 10/18/28), nicht als farbiger Rand.
 * - `vergleich` ist der Bezug („+12 % zum Sollwert", „56 von 57 Räumen").
 *   Die Richtung des Pfeils sagt nur, wohin sich der Wert bewegt hat -
 *   ob das gut ist, sagt `zustand`; bei einem Druckverlust ist „mehr"
 *   schlecht, bei einem Erfüllungsgrad gut.
 */

export interface KpiCardProps {
  titel: string;
  /** Zahl (wird deutsch formatiert) oder fertiger Knoten. */
  wert: number | string | null | undefined | ReactNode;
  einheit?: string;
  nachkomma?: number;
  /** Bezugsgröße, z. B. „+12 % zum Sollwert" oder „56 von 57 Räumen". */
  vergleich?: ReactNode;
  /** Richtung der Veränderung - rein beschreibend. */
  richtung?: 'hoch' | 'runter' | 'gleich';
  /** Bewertung. Ohne Angabe bleibt die Karte neutral (keine Ampel). */
  zustand?: Zustand;
  /** Text der Bewertungsmarke - Pflicht, sobald `zustand` gesetzt ist. */
  zustandText?: string;
  /** Sinnbild aus `Icons.tsx`, zurückhaltend eingesetzt. */
  icon?: ReactNode;
  /** Kleingedrucktes unter der Karte, z. B. die Norm. */
  fussnote?: ReactNode;
  /** Macht die ganze Karte zu einem Knopf (Sprung in die Liste dahinter). */
  beiKlick?: () => void;
  className?: string;
}

export function KpiCard({
  titel, wert, einheit, nachkomma, vergleich, richtung, zustand, zustandText,
  icon, fussnote, beiKlick, className,
}: KpiCardProps) {
  const einfach = typeof wert === 'number' || typeof wert === 'string'
    || wert === null || wert === undefined;

  const inhalt = (
    <>
      <div className="m-kpi-kopf">
        <span className="m-kpi-titel">{titel}</span>
        {icon && <span className="m-kpi-icon" aria-hidden="true">{icon}</span>}
      </div>

      <div className="m-kpi-wert">
        {einfach
          ? (
            <WertEinheit
              wert={wert as number | string | null | undefined}
              einheit={einheit}
              nachkomma={nachkomma}
              gross
            />
          )
          : (wert as ReactNode)}
      </div>

      {(vergleich || (zustand && zustandText)) && (
        <div className="m-kpi-fuss">
          {vergleich && (
            <span className="m-kpi-vergleich">
              {richtung && (
                <span className="m-kpi-pfeil" aria-hidden="true">
                  {richtung === 'hoch' ? '▲' : richtung === 'runter' ? '▼' : '■'}
                </span>
              )}
              {vergleich}
            </span>
          )}
          {zustand && zustandText && (
            <StatusBadge zustand={zustand} text={zustandText} klein />
          )}
        </div>
      )}

      {fussnote && <div className="m-kpi-fussnote">{fussnote}</div>}
    </>
  );

  if (beiKlick) {
    return (
      <button
        type="button"
        className={`m-kpi m-kpi-klickbar${className ? ` ${className}` : ''}`}
        onClick={beiKlick}
      >
        {inhalt}
      </button>
    );
  }

  return (
    <div className={`m-kpi${className ? ` ${className}` : ''}`}>{inhalt}</div>
  );
}

/**
 * Reihe aus Kennzahlkarten. Eigener Baustein, weil sonst jede Seite ihr
 * eigenes Gitter mit eigenen Abständen baut - genau das ist im Bestand
 * passiert (`.kennzahlen` in `styles.css` und daneben vier weitere
 * Eigenbauten).
 */
export interface KpiReiheProps {
  children: ReactNode;
  className?: string;
}

export function KpiReihe({ children, className }: KpiReiheProps) {
  return (
    <div className={`m-kpi-reihe${className ? ` ${className}` : ''}`}>
      {children}
    </div>
  );
}
